import { useState, useRef, useEffect, useCallback } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { loadGoogleMaps, fetchSuggestions, resolvePlace, SelectedPlace } from '@/hooks/useGoogleMaps';

interface Props {
  value: string;
  onChange: (val: string, place?: SelectedPlace) => void;
  placeholder?: string;
  label?: string;
  icon?: React.ReactNode;
}

const AddressAutocomplete = ({ value, onChange, placeholder, label, icon }: Props) => {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const tokenRef = useRef<any>(null);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    loadGoogleMaps().catch(() => {});
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const queryGoogle = useCallback(async (q: string) => {
    if (!q || q.length < 2) { setSuggestions([]); return; }
    setLoading(true);
    try {
      const { suggestions: s, token } = await fetchSuggestions(q, tokenRef.current);
      tokenRef.current = token;
      setSuggestions(s || []);
    } catch (e) {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInput = (val: string) => {
    onChange(val);
    setOpen(true);
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => queryGoogle(val), 250);
  };

  const handlePick = async (s: any) => {
    const pred = s.placePrediction;
    if (!pred) return;
    const text = pred.text?.toString() || '';
    onChange(text);
    setOpen(false);
    try {
      const place = await resolvePlace(pred.placeId, tokenRef.current);
      tokenRef.current = null; // new session next time
      onChange(place.description || text, place);
    } catch {
      // ignore – keep raw text
    }
  };

  return (
    <div ref={ref} className="relative">
      {label && <label className="block text-xs font-semibold text-foreground mb-1.5">{label}</label>}
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
          {icon || <MapPin className="w-4 h-4" />}
        </span>
        <input
          type="text"
          value={value}
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => value.length >= 2 && setOpen(true)}
          placeholder={placeholder}
          autoComplete="off"
          className="w-full pl-10 pr-9 py-3 rounded-xl border border-border bg-background text-foreground text-sm focus:ring-2 focus:ring-primary/50 outline-none"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
        )}
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-popover border border-border rounded-xl shadow-lg max-h-72 overflow-y-auto">
          {suggestions.map((s, i) => {
            const pred = s.placePrediction;
            if (!pred) return null;
            const main = pred.mainText?.toString() || pred.text?.toString() || '';
            const secondary = pred.secondaryText?.toString() || '';
            return (
              <button
                type="button"
                key={pred.placeId || i}
                onClick={() => handlePick(s)}
                className="w-full text-left px-4 py-2.5 text-sm hover:bg-muted flex items-start gap-2 transition-colors"
              >
                <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-foreground truncate">{main}</div>
                  {secondary && <div className="text-xs text-muted-foreground truncate">{secondary}</div>}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
